"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import en from "@/locale/en";
import { TbWorldCheck } from "react-icons/tb";
import { LuExternalLink } from "react-icons/lu";
import { useI18nContext } from "@/context/i18n-context";

type ProjectProps = (typeof en.projects.data)[number] & { index: number };

export default function Project({
  title,
  description,
  tags,
  imageUrl,
  imageFit,
  imageBg,
  repositoryUrl,
  repoType,
  demoType,
  demoUrl,
  index,
}: ProjectProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useI18nContext();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["0 1", "1.33 1"],
  });

  const scaleProgress = useTransform(scrollYProgress, [0, 1], [0.95, 1]);
  const opacityProgress = useTransform(scrollYProgress, [0, 1], [0.5, 1]);

  const hasLink = demoUrl && demoUrl !== "#";
  const hasRepo = repositoryUrl && repositoryUrl !== "";
  const isContain = imageFit === "contain";

  return (
    <motion.div
      ref={ref}
      style={{
        scale: scaleProgress,
        opacity: opacityProgress,
      }}
      className="w-full max-w-[42rem]"
    >
      <article
        className="group relative w-full rounded-2xl overflow-hidden
          bg-[var(--color-surface)]
          border border-[var(--color-border-subtle)]
          transition-all duration-300 ease-out
          hover:border-[var(--color-border)]
          hover:shadow-[0_8px_32px_oklch(0%_0_0_/_0.08)]
          dark:hover:shadow-[0_8px_32px_oklch(0%_0_0_/_0.32)]
          flex flex-col sm:h-[22rem] sm:flex-row"
      >
        {/* Content panel */}
        <div className="flex flex-col justify-between p-6 sm:p-8
          sm:w-[54%] sm:shrink-0 sm:h-full">
          {/* Index + title */}
          <div>
            <span className="block text-xs font-mono tracking-[0.2em] uppercase
              text-[var(--color-brand)] mb-2 tabular">
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="font-sans font-semibold text-xl leading-tight
              text-[var(--color-text-primary)] mb-3">
              {title}
            </h3>
            <p className="font-serif text-sm leading-relaxed
              text-[var(--color-text-secondary)]
              line-clamp-4">
              {description}
            </p>
          </div>

          {/* Bottom: tags + links */}
          <div className="mt-4">
            <ul className="flex flex-wrap gap-1.5 mb-4">
              {tags.map((tag, i) => (
                <li
                  key={i}
                  className="bg-[var(--color-tag-bg)] text-[var(--color-tag-text)]
                    text-[0.65rem] font-medium tracking-wider uppercase
                    px-2.5 py-1 rounded-full"
                >
                  {tag}
                </li>
              ))}
            </ul>

            {/* Action links */}
            {(hasLink || hasRepo) && (
              <div className="flex items-center gap-3">
                {hasLink && (
                  <a
                    href={demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5
                      text-xs font-medium
                      text-[var(--color-brand)]
                      hover:opacity-70
                      transition-opacity duration-150
                      focus-visible:outline-none focus-visible:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TbWorldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                    {demoType}
                  </a>
                )}
                {hasRepo && (
                  <a
                    href={repositoryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5
                      text-xs font-medium
                      text-[var(--color-text-tertiary)]
                      hover:text-[var(--color-text-secondary)]
                      transition-colors duration-150
                      focus-visible:outline-none focus-visible:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LuExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                    {repoType || "Repository"}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Brand logo / image panel */}
        <div
          className="relative w-full h-44 sm:h-auto sm:flex-1 overflow-hidden order-first sm:order-none"
          style={{ background: imageBg ?? "var(--color-surface-raised)" }}
        >
          {isContain ? (
            /* Logo treatment: centered, padded, object-contain, no scale-up on hover */
            <div className="absolute inset-0 flex items-center justify-center p-8 sm:p-10">
              <div className="relative w-full h-full">
                <Image
                  src={imageUrl}
                  alt={`${title} brand logo`}
                  fill
                  quality={90}
                  className="object-contain transition-opacity duration-300
                    group-hover:opacity-90"
                  sizes="(max-width: 640px) 100vw, 200px"
                />
              </div>
            </div>
          ) : (
            /* Photo treatment: full-bleed cover with subtle overlay */
            <>
              <div className="absolute inset-0 bg-[var(--color-border-subtle)] opacity-40 z-10 rounded-r-2xl" />
              <Image
                src={imageUrl}
                alt={`${title} preview`}
                fill
                quality={90}
                className="object-cover object-center
                  scale-[1.02]
                  transition-transform duration-500 ease-out
                  group-hover:scale-[1.06]"
                sizes="(max-width: 640px) 100vw, 200px"
              />
            </>
          )}
        </div>
      </article>
    </motion.div>
  );
}
